import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { useLocalStorageState } from 'ahooks';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import { useLoginUser } from 'Account/hooks/auth';
import { useLogout } from 'Account/hooks/account';
import Alert from 'Common/Components/Alert';
import Logo from 'Common/Components/Logo';
import { LocaleContext } from 'utils/OwnIntlProvider';
import PinForm from './views/PinForm';
import FormWrapper from './views/FormWrapper';
import SupportText from './views/SupportText';

const SupportTextWrapper = styled.div`
  margin-top: auto;
`;

const Title = styled.div`
  text-align: center;
  padding: 8px 0 16px;
  font-size: 12px;
  color: ${(props) => props.theme.fontColor};
  font-family: ${(props) => props.theme.fontFamily};
`;

type PinSubmissionProps = {
  pin: string;
};

const Pin: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const context = useContext(LocaleContext);
  const [account] = useLocalStorageState<AccountPersistInfo>('account');
  const pinLoginMutation = useLoginUser();
  const logoutMutation = useLogout();
  const [hasError, setHasError] = useState(false);

  const switchLanguage = (lng: string) => {
    localStorage.setItem('locale', lng);
    context.selectLanguage(lng);
  };

  const navigateToHome = () => {
    navigate('/');
  };

  const navigateToLogin = () => {
    navigate('/login');
    localStorage.removeItem('account');
    localStorage.removeItem('ledger-length');
    localStorage.removeItem('pre-accounting');
    localStorage.removeItem('newAppCredentials');
  };

  const onPinEnter = ({ pin }: PinSubmissionProps) => {
    pinLoginMutation.mutate(
      {
        username: account?.email as string,
        pin,
        accountToken: account?.accountToken as string,
      },
      {
        onSuccess: ({ token, error }) => {
          if (token) navigateToHome();
          else if (error) {
            setHasError(true);
          }
        },
      }
    );
  };

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => navigateToLogin(),
    });
  };
  return (
    <FormWrapper>
      <Logo padding="16px" />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          marginTop: '38px',
          width: '100%',
        }}
      >
        <Title>
          <FormattedMessage defaultMessage="Enter your PIN" />
        </Title>
        {hasError && (
          <Alert
            title={intl.formatMessage({
              defaultMessage:
                'Please try again. You have entered an invalid PIN.',
            })}
            type="error"
          />
        )}
        <PinForm
          checking={pinLoginMutation.isPending}
          email={account?.email as string}
          onPinEnter={onPinEnter}
          error={hasError}
          onLogout={handleLogout}
        />
      </div>
      <SupportTextWrapper>
        <SupportText
          selectedLang={context.locale}
          changeLang={switchLanguage}
        />
      </SupportTextWrapper>
    </FormWrapper>
  );
};

export default Pin;
